import { createTheme } from '@mui/material/styles';

/**
 * Professional Monochromatic Theme - Black, White & Grays Only
 * All colors neutralized to prevent ANY colored accents
 */
const monochromeTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#212121',
      light: '#616161',
      dark: '#000000',
      contrastText: '#ffffff'
    },
    secondary: {
      main: '#757575',
      light: '#9e9e9e',
      dark: '#424242',
      contrastText: '#ffffff'
    },
    background: {
      default: '#ffffff',
      paper: '#fafafa'
    },
    text: {
      primary: '#212121',
      secondary: '#616161',
      disabled: '#9e9e9e'
    },
    divider: '#e0e0e0',
    error: {
      main: '#424242',
      light: '#757575',
      dark: '#212121',
      contrastText: '#ffffff'
    },
    warning: {
      main: '#757575',
      light: '#9e9e9e',
      dark: '#424242',
      contrastText: '#ffffff'
    },
    info: {
      main: '#616161',
      light: '#9e9e9e',
      dark: '#424242',
      contrastText: '#ffffff'
    },
    success: {
      main: '#424242',
      light: '#757575',
      dark: '#212121',
      contrastText: '#ffffff'
    },
    action: {
      active: '#212121',
      hover: 'rgba(0, 0, 0, 0.04)',
      hoverOpacity: 0.04,
      selected: 'rgba(0, 0, 0, 0.08)',
      selectedOpacity: 0.08,
      disabled: '#bdbdbd',
      disabledBackground: '#f5f5f5',
      disabledOpacity: 0.38,
      focus: 'rgba(0, 0, 0, 0.12)',
      focusOpacity: 0.12,
      activatedOpacity: 0.12
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700, color: '#212121' },
    h2: { fontWeight: 700, color: '#212121' },
    h3: { fontWeight: 600, color: '#212121' },
    h4: { fontWeight: 600, color: '#212121' },
    h5: { fontWeight: 600, color: '#212121' },
    h6: { fontWeight: 600, color: '#212121' },
    subtitle1: { fontWeight: 500, color: '#616161' },
    subtitle2: { fontWeight: 500, color: '#757575' },
    body1: { color: '#212121' },
    body2: { color: '#616161' },
    button: { fontWeight: 600, textTransform: 'none' }
  },
  shape: {
    borderRadius: 4
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0,0,0,0.08)',
    '0px 1px 4px rgba(0,0,0,0.08)',
    '0px 2px 4px rgba(0,0,0,0.10)',
    '0px 2px 8px rgba(0,0,0,0.10)',
    '0px 4px 8px rgba(0,0,0,0.12)',
    '0px 4px 12px rgba(0,0,0,0.12)',
    '0px 6px 12px rgba(0,0,0,0.14)',
    '0px 6px 16px rgba(0,0,0,0.14)',
    '0px 8px 16px rgba(0,0,0,0.16)',
    '0px 8px 20px rgba(0,0,0,0.16)',
    '0px 10px 20px rgba(0,0,0,0.18)',
    '0px 10px 24px rgba(0,0,0,0.18)',
    '0px 12px 24px rgba(0,0,0,0.20)',
    '0px 12px 28px rgba(0,0,0,0.20)',
    '0px 14px 28px rgba(0,0,0,0.22)',
    '0px 14px 32px rgba(0,0,0,0.22)',
    '0px 16px 32px rgba(0,0,0,0.24)',
    '0px 16px 36px rgba(0,0,0,0.24)',
    '0px 18px 36px rgba(0,0,0,0.26)',
    '0px 18px 40px rgba(0,0,0,0.26)',
    '0px 20px 40px rgba(0,0,0,0.28)',
    '0px 20px 44px rgba(0,0,0,0.28)',
    '0px 22px 44px rgba(0,0,0,0.30)',
    '0px 22px 48px rgba(0,0,0,0.30)'
  ],
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false
      },
      styleOverrides: {
        root: {
          '& .MuiTouchRipple-root': {
            color: '#9e9e9e'
          }
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          '& .MuiTouchRipple-root': {
            color: '#9e9e9e'
          }
        },
        containedPrimary: {
          backgroundColor: '#212121',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#424242'
          }
        },
        outlinedPrimary: {
          borderColor: '#212121',
          color: '#212121',
          '&:hover': {
            borderColor: '#000000',
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#616161',
          '&:hover': {
            backgroundColor: '#f5f5f5',
            color: '#212121'
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: '#212121',
          color: '#ffffff'
        },
        colorSecondary: {
          backgroundColor: '#757575',
          color: '#ffffff'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid #e0e0e0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          border: '1px solid #e0e0e0'
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          margin: 16,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100dvh - 32px)'
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          background: '#fafafa',
          border: '1px solid #e0e0e0',
          color: '#212121'
        },
        standardError: {
          background: '#f5f5f5',
          border: '1px solid #bdbdbd',
          color: '#212121'
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              borderColor: '#212121'
            }
          }
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: '#000000',
          color: '#ffffff'
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#fafafa',
            fontWeight: 600,
            borderBottom: '2px solid #e0e0e0'
          }
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': {
            backgroundColor: '#f5f5f5'
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e0e0e0'
        }
      }
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          overflowX: 'auto'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          color: '#616161',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          },
          '&.Mui-selected': {
            color: '#000000',
            fontWeight: 600
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 64
        },
        indicator: {
          backgroundColor: '#000000',
          height: 3
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          background: '#e0e0e0'
        },
        barColorPrimary: {
          background: '#212121'
        }
      }
    },
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: '#212121'
        }
      }
    },
    MuiBadge: {
      styleOverrides: {
        colorPrimary: {
          background: '#212121',
          color: '#ffffff'
        }
      }
    }
  }
});

export default monochromeTheme;
